import {
  DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  useDebounce,
} from '@ipfabric/ui-components/hooks/useDebounce';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { useLocale } from 'containers/App/LocaleContext';
import { useEffect, useMemo } from 'react';
import { useApi } from 'utils/request/useApi';
import { type Vrf, fetchVrf } from './fetchVrf';
import { getVrfOption } from './helpers/getVrfOption';

interface GraphPathlookupFormHook {
  handleProtocolChange: (value: string | null) => void;
}

interface Props {
  updateVrf: (vrf: Vrf[]) => void;
  updateFieldValue: (field: string, value: any) => void;
  loadedFromLocalStorage: boolean;
  vrf: Vrf[];
  vrfValue: Vrf;
  vrfIpValue: string;
  vrfIpError: string | undefined;
}

/**
 * Tackles these concerns common to graph path lookup forms:
 *  - there is a bunch of ip fields with typeahead
 *  - one of these fields is bound to vrf select
 *  - reset flags field when icmp protocol is selected (this can be just ignored for
 *    host to gateway pathlookup which does not involve protocol selection)
 */
export const useGraphPathlookupForm = (
  props: Props,
): GraphPathlookupFormHook => {
  const apiRequest = useApi().request;
  const ctxProps = useGraphContextSelectors({
    snapshotId: prop('snapshotId'),
  });
  const msg = useLocale();
  const debounceVrfSearch = useDebounce(DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT);

  const handleProtocolChange = useStableCallback((value: string | null) => {
    if (value === 'icmp' || value === 'icmpv6') {
      props.updateFieldValue('flags', []);
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: debounced search on vrf input change
  useEffect(() => {
    const maybeFetchVrf = async () => {
      if (!props.vrfIpError) {
        const vrfResponse = await fetchVrf(
          apiRequest,
          ctxProps.snapshotId,
          props.vrfIpValue,
        );
        props.updateVrf(vrfResponse);
        if (!vrfResponse.includes(props.vrfValue)) {
          props.updateFieldValue(
            'vrf',
            // Fixed noUncheckedIndexedAccess NIM-19408
            vrfResponse.length
              ? getVrfOption(vrfResponse[0]!, msg).value
              : null,
          );
        }
      }
    };

    debounceVrfSearch(maybeFetchVrf);
  }, [props.vrfIpValue, props.vrfIpError]);

  return useMemo(() => ({ handleProtocolChange }), []);
};
